<template>
  <BaseModal
    v-model="showModal"
    title="Encaissement"
    size="5xl"
    :closable="!isProcessing"
    @close="closeModal"
  >
    <template #header>
      <div class="flex items-center gap-3">
        <div class="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center text-primary">
          <BaseIcon name="cash" :size="18" />
        </div>
        <div>
          <h2 class="text-lg font-semibold text-gray-900">Encaissement de la vente</h2>
          <p class="text-xs text-gray-400">{{ sale.invoice_number }}</p>
        </div>
      </div>
    </template>

    <div class="space-y-5">
      <!-- Message d'erreur -->
      <div v-if="errorMessage" class="bg-red-50 border border-red-100 rounded-lg p-3 flex items-start gap-2.5">
        <BaseIcon name="exclamation-circle" :size="15" class="text-red-500 mt-0.5 flex-shrink-0" />
        <span class="text-sm text-red-600">{{ errorMessage }}</span>
      </div>

      <!-- Resume de la vente -->
      <div class="bg-gray-50 rounded-xl p-4 border border-gray-100">
        <div class="flex justify-between items-center py-2 border-b border-gray-100">
          <span class="text-sm text-gray-500">Client</span>
          <span class="text-sm font-semibold text-gray-900">{{ customer?.name || 'Client anonyme' }}</span>
        </div>
        <div class="flex justify-between items-center py-2 border-b border-gray-100">
          <span class="text-sm text-gray-500">Date de vente</span>
          <span class="text-sm font-medium text-gray-700">{{ formatDate(sale.sale_date) }}</span>
        </div>
        <div class="flex justify-between items-center py-2 border-b border-gray-100">
          <span class="text-sm text-gray-500">Montant total</span>
          <span class="text-sm font-semibold text-gray-900">{{ formatCurrency(sale.total_incl_tax) }}</span>
        </div>
        <div class="flex justify-between items-center py-2 border-b border-gray-100">
          <span class="text-sm text-gray-500">Deja paye</span>
          <span class="text-sm font-semibold text-green-600">{{ formatCurrency(paidAmount) }}</span>
        </div>
        <div class="flex justify-between items-center pt-3 mt-1">
          <span class="text-sm font-medium text-gray-700">Reste a payer</span>
          <span class="text-lg font-bold text-gray-900">{{ formatCurrency(remainingAmount) }}</span>
        </div>
      </div>

      <!-- Montant a payer -->
      <div>
        <h4 class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Montant du paiement</h4>

        <div class="space-y-3">
          <div>
            <label class="block text-sm font-medium text-gray-600 mb-1.5">
              Montant a payer <span class="text-red-500">*</span>
            </label>
            <div class="relative">
              <input
                v-model.number="paymentData.amount"
                type="number"
                :max="remainingAmount"
                min="1"
                step="1"
                class="w-full px-4 py-2.5 pr-16 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-primary focus:bg-white focus:ring-2 focus:ring-primary/20 transition-all"
                :class="{ 'border-red-400': validationErrors.amount }"
                :placeholder="`Maximum: ${formatCurrency(remainingAmount)}`"
              />
              <span class="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-400">FCFA</span>
            </div>
            <span v-if="validationErrors.amount" class="text-xs text-red-500 mt-1 block">{{ validationErrors.amount }}</span>
            <button
              type="button"
              class="w-full mt-2 py-2 px-4 text-sm font-medium text-primary bg-primary-50 rounded-lg hover:bg-primary hover:text-white transition-colors"
              @click="paymentData.amount = remainingAmount"
            >
              Payer le solde complet ({{ formatCurrency(remainingAmount) }})
            </button>
          </div>
        </div>
      </div>

      <!-- Methode de paiement -->
      <div>
        <h4 class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
          Mode de paiement <span class="text-red-500">*</span>
        </h4>

        <div class="grid grid-cols-3 sm:grid-cols-4 gap-2">
          <button
            v-for="method in availablePaymentMethods"
            :key="method.id"
            type="button"
            class="flex flex-col items-center gap-1.5 px-2 py-3 bg-white border-2 rounded-lg cursor-pointer transition-all text-center disabled:opacity-50 disabled:cursor-not-allowed"
            :class="[
              paymentData.payment_type === method.id
                ? (method.id === 'wallet' ? 'border-violet-500 bg-violet-50' : 'border-primary bg-primary-50')
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            ]"
            :disabled="method.id === 'wallet' && !canPayWithWallet"
            @click="selectPaymentMethod(method.id)"
          >
            <BaseIcon
              :name="method.icon"
              :size="18"
              :class="paymentData.payment_type === method.id
                ? (method.id === 'wallet' ? 'text-violet-600' : 'text-primary')
                : 'text-gray-400'"
            />
            <span class="text-[11px] font-medium text-gray-600">{{ method.name }}</span>
            <span v-if="method.id === 'wallet' && customerWallet"
              class="text-[10px] font-semibold text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full">
              {{ formatCurrency(customerWallet.balance) }}
            </span>
            <span v-if="method.id === 'wallet' && !canPayWithWallet"
              class="text-[10px] text-red-500">
              Solde insuffisant
            </span>
          </button>
        </div>
        <span v-if="validationErrors.payment_type" class="text-xs text-red-500 mt-2 block">{{ validationErrors.payment_type }}</span>
      </div>

      <!-- Date et notes -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1.5">Date du paiement</label>
          <input
            type="datetime-local"
            v-model="paymentData.payment_date"
            class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-primary focus:bg-white focus:ring-2 focus:ring-primary/20 transition-all"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1.5">Notes (optionnel)</label>
          <input
            v-model="paymentData.notes"
            type="text"
            class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-primary focus:bg-white focus:ring-2 focus:ring-primary/20 transition-all"
            placeholder="Reference, details..."
            maxlength="500"
          />
        </div>
      </div>

      <!-- Recapitulatif -->
      <div v-if="paymentData.amount > 0 && paymentData.payment_type" class="bg-green-50 rounded-xl p-4 border border-green-100">
        <h4 class="text-xs font-semibold text-green-700 uppercase tracking-wider mb-3">Recapitulatif du paiement</h4>
        <div class="space-y-2">
          <div class="flex justify-between items-center">
            <span class="text-sm text-green-600">Montant</span>
            <span class="text-sm font-bold text-green-700">{{ formatCurrency(paymentData.amount) }}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-sm text-green-600">Mode</span>
            <span class="text-sm font-medium text-green-700">{{ getMethodName(paymentData.payment_type) }}</span>
          </div>
          <div v-if="paymentData.amount < remainingAmount" class="flex justify-between items-center pt-2 border-t border-green-100">
            <span class="text-sm text-green-600">Reste apres paiement</span>
            <span class="text-sm font-medium text-orange-600">{{ formatCurrency(remainingAmount - paymentData.amount) }}</span>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full">
        <span class="text-xs text-gray-400">* Champs obligatoires</span>
        <div class="flex gap-3">
          <BaseButton variant="ghost" :disabled="isProcessing" @click="closeModal">
            Annuler
          </BaseButton>
          <BaseButton
            variant="primary"
            :loading="isProcessing"
            :disabled="!canProcess"
            @click="processPayment"
          >
            <template #icon-left>
              <BaseIcon name="check" :size="16" />
            </template>
            Confirmer le paiement
          </BaseButton>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import { defineComponent, ref, computed, watch } from 'vue';
import paymentService from '@/services/paymentService';
import customerService from '@/services/customerService';
import CashRegisterService from '@/services/cashRegisterService';
import { useToastStore } from '@/store/modules/toastStore';
import { BaseModal, BaseButton, BaseIcon } from '@/components/base';

export default defineComponent({
  name: 'PaymentModal',
  components: {
    BaseModal,
    BaseButton,
    BaseIcon
  },
  props: {
    show: {
      type: Boolean,
      required: true
    },
    sale: {
      type: Object,
      required: true
    },
    customer: {
      type: Object,
      required: false
    },
    wallet: {
      type: Object,
      required: false
    }
  },
  emits: ['close', 'payment-success'],
  setup(props, { emit }) {
    const toastStore = useToastStore();
    const isProcessing = ref(false);
    const errorMessage = ref('');
    const validationErrors = ref({});
    const customerWallet = ref(null);
    const walletLoading = ref(false);

    const paymentData = ref({
      sale_id: '',
      amount: 0,
      payment_type: '',
      payment_date: '',
      cash_register_id: '',
      notes: ''
    });

    const paymentMethods = [
      { id: 'cash', name: 'Especes', icon: 'cash' },
      { id: 'card', name: 'Carte bancaire', icon: 'credit-card' },
      { id: 'wave', name: 'Wave', icon: 'phone' },
      { id: 'orange_money', name: 'Orange Money', icon: 'phone' },
      { id: 'mobile_money', name: 'Mobile Money', icon: 'phone' },
      { id: 'bank_transfer', name: 'Virement', icon: 'bank' },
      { id: 'check', name: 'Cheque', icon: 'receipt' },
      { id: 'wallet', name: 'Portefeuille', icon: 'wallet' }
    ];

    const showModal = computed({
      get: () => props.show,
      set: (value) => {
        if (!value) {
          emit('close');
        }
      }
    });

    const paidAmount = computed(() => {
      if (props.sale.payments && props.sale.payments.length > 0) {
        return props.sale.payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
      }
      return props.sale.paid_amount || 0;
    });

    const remainingAmount = computed(() => {
      return (props.sale.total_incl_tax || 0) - paidAmount.value;
    });

    const canPayWithWallet = computed(() => {
      if (!customerWallet.value) return false;
      return customerWallet.value.balance >= paymentData.value.amount && paymentData.value.amount > 0;
    });

    const availablePaymentMethods = computed(() => {
      if (props.customer && props.wallet) {
        return paymentMethods;
      }
      return paymentMethods.filter(m => m.id !== 'wallet');
    });

    const canProcess = computed(() => {
      return paymentData.value.payment_type &&
        paymentData.value.amount > 0 &&
        paymentData.value.amount <= remainingAmount.value &&
        props.sale.id;
    });

    const loadCustomerWallet = async () => {
      if (!props.wallet) {
        customerWallet.value = null;
        return;
      }

      try {
        walletLoading.value = true;
        const response = await customerService.getCustomerWallet(props.wallet.customer_id);
        customerWallet.value = response.data.data;
      } catch (error) {
        console.error('Erreur lors du chargement du portefeuille:', error);
        customerWallet.value = null;
      } finally {
        walletLoading.value = false;
      }
    };

    const selectPaymentMethod = (methodId) => {
      if (methodId === 'wallet' && !canPayWithWallet.value) return;
      paymentData.value.payment_type = methodId;
      validationErrors.value.payment_type = '';
    };

    const getMethodName = (methodId) => {
      const method = paymentMethods.find(m => m.id === methodId);
      return method ? method.name : '';
    };

    const initializePaymentData = () => {
      const now = new Date();
      const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);

      paymentData.value = {
        sale_id: props.sale.id,
        amount: remainingAmount.value,
        payment_type: '',
        payment_date: localDateTime,
        cash_register_id: '',
        notes: ''
      };
      errorMessage.value = '';
      validationErrors.value = {};

      loadCustomerWallet();
    };

    const validateForm = () => {
      validationErrors.value = {};
      let isValid = true;

      if (!paymentData.value.amount || paymentData.value.amount <= 0) {
        validationErrors.value.amount = 'Le montant doit etre superieur a 0';
        isValid = false;
      } else if (paymentData.value.amount > remainingAmount.value) {
        validationErrors.value.amount = `Le montant ne peut pas depasser ${formatCurrency(remainingAmount.value)}`;
        isValid = false;
      }

      if (!paymentData.value.payment_type) {
        validationErrors.value.payment_type = 'Veuillez selectionner un mode de paiement';
        isValid = false;
      }

      if (paymentData.value.payment_type === 'wallet' && customerWallet.value) {
        if (customerWallet.value.balance < paymentData.value.amount) {
          validationErrors.value.payment_type = 'Solde du portefeuille insuffisant';
          isValid = false;
        }
      }

      return isValid;
    };

    const processPayment = async () => {
      if (isProcessing.value) return;
      isProcessing.value = true;

      if (!validateForm()) {
        isProcessing.value = false;
        return;
      }

      if (paymentData.value.payment_type !== 'wallet') {
        try {
          const cashRegister = await CashRegisterService.getMyOpenedCashRegister();
          paymentData.value.cash_register_id = cashRegister.data.id;
        } catch (error) {
          console.error('Erreur lors de la recuperation de la caisse:', error);
          errorMessage.value = error.message || 'Aucune caisse ouverte trouvee';
          isProcessing.value = false;
          return;
        }
      }

      try {
        errorMessage.value = '';

        const requestData = {
          sale_id: paymentData.value.sale_id,
          amount: paymentData.value.amount,
          payment_type: paymentData.value.payment_type,
          payment_date: paymentData.value.payment_date || undefined,
          notes: paymentData.value.notes || undefined
        };

        if (paymentData.value.payment_type !== 'wallet') {
          requestData.cash_register_id = paymentData.value.cash_register_id;
        }

        await paymentService.makePayment(requestData);

        toastStore.addToast({
          type: 'success',
          message: 'Paiement enregistre avec succes',
          duration: 4000
        });

        emit('payment-success');
      } catch (error) {
        console.error('Erreur lors du paiement:', error);
        errorMessage.value = error.response?.data?.message || 'Erreur lors du paiement';
        toastStore.addToast({
          type: 'error',
          message: error.response?.data?.message || 'Erreur lors du paiement',
          duration: 4000
        });
        isProcessing.value = false;
      }
    };

    const closeModal = () => {
      emit('close');
    };

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XOF',
        minimumFractionDigits: 0
      }).format(amount || 0);
    };

    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    };

    watch(() => props.show, (newVal) => {
      if (newVal) {
        initializePaymentData();
      }
    });

    watch(() => paymentData.value.amount, () => {
      if (paymentData.value.payment_type === 'wallet' && !canPayWithWallet.value) {
        paymentData.value.payment_type = '';
      }
    });

    return {
      showModal,
      isProcessing,
      paymentData,
      errorMessage,
      validationErrors,
      customerWallet,
      walletLoading,
      paymentMethods,
      availablePaymentMethods,
      paidAmount,
      remainingAmount,
      canPayWithWallet,
      canProcess,
      selectPaymentMethod,
      getMethodName,
      processPayment,
      closeModal,
      formatCurrency,
      formatDate
    };
  }
});
</script>
